import { useMutation } from "@tanstack/react-query";
import {
  AlertCircle,
  Calculator,
  FileSpreadsheet,
  FileText,
  Loader2,
  X,
} from "lucide-react";
import { useState } from "react";
import { api, baixarArquivoPost } from "../lib/api";


interface Props {
  condominioId: number;
  condominioNome?: string;
  onFechar: () => void;
}

interface Coluna { indice: number; label: string; valor_parcela: number; }
interface Linha { unidade: string; fracao: number; total: number; parcelas: number[]; }
interface Tabela {
  valor_total: number;
  soma_fracao: number;
  aviso: string | null;
  colunas: Coluna[];
  linhas: Linha[];
  totais_por_coluna: number[];
  total_geral: number;
}

const brl = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

// "1.234,56" ou "1234.56" -> número
function moedaParaNum(s: string): number {
  const t = s.trim().replace(/\./g, "").replace(",", ".");
  const n = Number(t);
  return Number.isFinite(n) ? n : 0;
}

export function ChamadaExtraModal({ condominioId, condominioNome, onFechar }: Props) {
  const [valorTotal, setValorTotal] = useState("");
  const [nParcelas, setNParcelas] = useState("1");
  const [mesInicial, setMesInicial] = useState("");
  const [personalizar, setPersonalizar] = useState(false);
  const [modoParcela, setModoParcela] = useState<"valor" | "percentual">("valor");
  const [parcelasCustom, setParcelasCustom] = useState<string[]>([]);
  const [tabela, setTabela] = useState<Tabela | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [baixado, setBaixado] = useState(false);

  const valorNum = moedaParaNum(valorTotal);
  const nNum = Math.max(1, parseInt(nParcelas || "1", 10) || 1);

  // Valores-padrão de cada parcela conforme o modo (divisão igual).
  function defaultsParcela(modo: "valor" | "percentual", n: number): string[] {
    if (modo === "percentual") {
      const base = 100 / n;
      return Array.from({ length: n }, () => base.toFixed(4).replace(".", ","));
    }
    const base = valorNum > 0 ? valorNum / n : 0;
    return Array.from({ length: n }, () => base.toFixed(2).replace(".", ","));
  }
  // Ao ligar "personalizar", inicia com divisão igual (no modo atual).
  function togglePersonalizar(on: boolean) {
    setPersonalizar(on);
    if (on) setParcelasCustom(defaultsParcela(modoParcela, nNum));
  }
  function trocarModo(modo: "valor" | "percentual") {
    setModoParcela(modo);
    if (personalizar) setParcelasCustom(defaultsParcela(modo, nNum));
  }

  // Converte o que o usuário digitou em VALORES (R$) de cada parcela.
  // No modo percentual, aplica % sobre o total e ajusta o centavo residual na última.
  function valoresPersonalizados(): number[] {
    if (modoParcela === "valor") return parcelasCustom.map(moedaParaNum);
    const pcts = parcelasCustom.map(moedaParaNum);
    const vals = pcts.map((p) => Math.round(valorNum * p) / 100); // total*p/100 arredondado a centavo
    const soma = vals.reduce((a, b) => a + b, 0);
    const resid = Math.round((valorNum - soma) * 100) / 100;
    if (vals.length) vals[vals.length - 1] = Math.round((vals[vals.length - 1] + resid) * 100) / 100;
    return vals;
  }

  const somaCustomValor = valoresPersonalizados().reduce((a, b) => a + b, 0);
  const somaCustomPct = parcelasCustom.reduce((a, s) => a + moedaParaNum(s), 0);
  const customOk = !personalizar
    ? true
    : modoParcela === "percentual"
      ? Math.abs(somaCustomPct - 100) < 0.01
      : Math.abs(somaCustomValor - valorNum) < 0.01;

  function montarPayload() {
    const p: Record<string, unknown> = {
      valor_total: valorNum,
      n_parcelas: nNum,
      mes_inicial: mesInicial.trim() || null,
    };
    if (personalizar) p.parcelas_customizadas = valoresPersonalizados();
    return p;
  }

  const simular = useMutation({
    mutationFn: () =>
      api.post<Tabela>(`/condominios/${condominioId}/chamada-extra/simular`, montarPayload()),
    onSuccess: (t) => { setTabela(t); setErro(null); setBaixado(false); },
    onError: (e: unknown) => {
      setTabela(null);
      setErro(e instanceof Error ? e.message : "Não foi possível calcular.");
    },
  });

  const exportar = useMutation({
    mutationFn: async (formato: "xlsx" | "pdf") => {
      // Baixa o arquivo pelo navegador (o nome vem do servidor).
      await baixarArquivoPost(
        `/condominios/${condominioId}/chamada-extra/arquivo`,
        { ...montarPayload(), formato },
        `chamada-extra.${formato}`,
      );
    },
    onSuccess: () => {
      setErro(null);
      setBaixado(true);
    },
    onError: (e: unknown) =>
      setErro(e instanceof Error ? e.message : "Não foi possível gerar o arquivo."),
  });

  const podeGerar = valorNum > 0 && (!personalizar || customOk);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col">
        {/* Cabeçalho */}
        <div className="flex items-start justify-between p-5 border-b border-slate-200">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Tabela de Chamada Extra</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {condominioNome ? `${condominioNome} · ` : ""}
              rateio por fração ideal, dividido em parcelas (meses).
            </p>
          </div>
          <button type="button" onClick={onFechar} className="text-slate-400 hover:text-slate-700">
            <X size={20} />
          </button>
        </div>

        {/* Corpo */}
        <div className="p-5 overflow-y-auto">
          {/* Entradas */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="text-sm">
              <span className="block text-slate-600 mb-1">Valor total da chamada (R$)</span>
              <input
                value={valorTotal}
                onChange={(e) => setValorTotal(e.target.value)}
                placeholder="Ex.: 30.000,00"
                inputMode="decimal"
                className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm tabular-nums"
              />
            </label>
            <label className="text-sm">
              <span className="block text-slate-600 mb-1">Nº de parcelas</span>
              <input
                type="number" min={1} max={60}
                value={nParcelas}
                onChange={(e) => { setNParcelas(e.target.value); if (personalizar) togglePersonalizar(true); }}
                className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm"
              />
            </label>
            <label className="text-sm">
              <span className="block text-slate-600 mb-1">Mês da 1ª parcela</span>
              <input
                value={mesInicial}
                onChange={(e) => setMesInicial(e.target.value)}
                placeholder="AAAA-MM (ex.: 2026-08)"
                className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm"
              />
            </label>
          </div>

          {/* Personalizar parcelas */}
          <div className="mt-3">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={personalizar}
                onChange={(e) => togglePersonalizar(e.target.checked)}
              />
              Personalizar o valor de cada parcela (senão, divide igual)
            </label>
            {personalizar && (
              <div className="mt-2">
                {/* Escolha: informar cada parcela por valor OU por percentual */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-2 text-xs">
                  <span className="text-slate-600">Informar cada parcela por:</span>
                  <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input
                      type="radio"
                      checked={modoParcela === "valor"}
                      onChange={() => trocarModo("valor")}
                    />
                    Valor (R$)
                  </label>
                  <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input
                      type="radio"
                      checked={modoParcela === "percentual"}
                      onChange={() => trocarModo("percentual")}
                    />
                    Percentual (%)
                  </label>
                </div>

                <div className="flex flex-wrap gap-2">
                  {parcelasCustom.map((v, i) => (
                    <label key={i} className="text-xs text-slate-600">
                      <span className="block mb-0.5">Parcela {i + 1}</span>
                      <div className="relative">
                        <input
                          value={v}
                          onChange={(e) =>
                            setParcelasCustom((arr) => arr.map((x, k) => (k === i ? e.target.value : x)))
                          }
                          inputMode="decimal"
                          className={`w-28 px-2 py-1 ${modoParcela === "percentual" ? "pr-6" : ""} border border-slate-300 rounded-md text-sm text-right tabular-nums`}
                        />
                        {modoParcela === "percentual" && (
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">%</span>
                        )}
                      </div>
                    </label>
                  ))}
                </div>

                {modoParcela === "percentual" ? (
                  <p className={`mt-1 text-xs font-medium ${customOk ? "text-emerald-700" : "text-rose-700"}`}>
                    Soma dos percentuais: {somaCustomPct.toFixed(2).replace(".", ",")}%{" "}
                    {customOk ? "✓ (= 100%)" : "— deve dar 100%"}
                    {" · equivale a "}{brl(somaCustomValor)}
                  </p>
                ) : (
                  <p className={`mt-1 text-xs font-medium ${customOk ? "text-emerald-700" : "text-rose-700"}`}>
                    Soma das parcelas: {brl(somaCustomValor)}{" "}
                    {customOk ? "✓" : `— deve ser igual ao total (${brl(valorNum)})`}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Ações */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={!podeGerar || simular.isPending}
              onClick={() => simular.mutate()}
              className="inline-flex items-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium rounded-md"
            >
              {simular.isPending ? <Loader2 size={15} className="animate-spin" /> : <Calculator size={15} />}
              Calcular prévia
            </button>
            <button
              type="button"
              disabled={!podeGerar || exportar.isPending}
              onClick={() => exportar.mutate("xlsx")}
              className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium rounded-md"
            >
              {exportar.isPending ? <Loader2 size={15} className="animate-spin" /> : <FileSpreadsheet size={15} />}
              Exportar Excel
            </button>
            <button
              type="button"
              disabled={!podeGerar || exportar.isPending}
              onClick={() => exportar.mutate("pdf")}
              className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-50 text-slate-700 text-sm font-medium rounded-md"
            >
              <FileText size={15} /> Exportar PDF
            </button>
          </div>

          {erro && (
            <div className="mt-3 flex items-start gap-2 text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" /> {erro}
            </div>
          )}

          {baixado && (
            <div className="mt-3 text-sm bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
              <p className="text-emerald-800">
                Arquivo baixado! Veja na pasta <b>Downloads</b> do navegador — de lá você
                anexa ao e-mail ou WhatsApp.
              </p>
            </div>
          )}

          {/* Prévia */}
          {tabela && (
            <div className="mt-5">
              {tabela.aviso && (
                <div className="mb-2 flex items-start gap-2 text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
                  <AlertCircle size={14} className="flex-shrink-0 mt-0.5" /> {tabela.aviso}
                </div>
              )}
              <div className="overflow-x-auto border border-slate-200 rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-800 text-white text-xs">
                    <tr>
                      <th className="text-left px-3 py-2">Unidade</th>
                      <th className="text-center px-3 py-2">Fração</th>
                      {tabela.colunas.map((c) => (
                        <th key={c.indice} className="text-right px-3 py-2">{c.label}</th>
                      ))}
                      <th className="text-right px-3 py-2">Total</th>
                    </tr>
                    <tr className="bg-slate-100 text-slate-500 text-[11px] italic">
                      <td className="px-3 py-1 text-right" colSpan={2}>Valor da parcela →</td>
                      {tabela.colunas.map((c) => (
                        <td key={c.indice} className="px-3 py-1 text-right tabular-nums">{brl(c.valor_parcela)}</td>
                      ))}
                      <td className="px-3 py-1 text-right tabular-nums">{brl(tabela.valor_total)}</td>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {tabela.linhas.map((l, i) => (
                      <tr key={l.unidade} className={i % 2 ? "bg-slate-50" : ""}>
                        <td className="px-3 py-1.5 font-medium text-slate-900">{l.unidade}</td>
                        <td className="px-3 py-1.5 text-center tabular-nums text-slate-500">
                          {(l.fracao * 100).toFixed(4).replace(".", ",")}%
                        </td>
                        {l.parcelas.map((v, k) => (
                          <td key={k} className="px-3 py-1.5 text-right tabular-nums">{brl(v)}</td>
                        ))}
                        <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{brl(l.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-800 text-white font-semibold">
                    <tr>
                      <td className="px-3 py-2">TOTAL</td>
                      <td className="px-3 py-2 text-center tabular-nums">
                        {(tabela.soma_fracao * 100).toFixed(2).replace(".", ",")}%
                      </td>
                      {tabela.totais_por_coluna.map((t, k) => (
                        <td key={k} className="px-3 py-2 text-right tabular-nums">{brl(t)}</td>
                      ))}
                      <td className="px-3 py-2 text-right tabular-nums">{brl(tabela.total_geral)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Rodapé */}
        <div className="flex items-center justify-end p-4 border-t border-slate-200">
          <button type="button" onClick={onFechar} className="px-3 py-2 text-sm text-slate-600 hover:text-slate-900">
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
